import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { AU_STATES, isValidABN } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";

const EMPTY = {
  customer_type: "business", company_name: "", abn: "", contact_name: "", email: "", phone: "", mobile: "",
  street: "", suburb: "", state: "NSW", postcode: "", site_same_as_billing: true,
  site_street: "", site_suburb: "", site_state: "NSW", site_postcode: "", notes: "",
};

const Field = ({ label, children, className = "" }) => (
  <div className={className}><Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-zinc-500">{label}</Label>{children}</div>
);

const StateSelect = ({ value, onChange, testId }) => (
  <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
    {AU_STATES.map((s) => <option key={s}>{s}</option>)}
  </select>
);

const AddressFields = ({ f, set, prefix = "" }) => (
  <div className="grid grid-cols-6 gap-3">
    <Field label="Street" className="col-span-6"><Input data-testid={`${prefix}street-input`} value={f[`${prefix}street`]} onChange={(e) => set(`${prefix}street`, e.target.value)} /></Field>
    <Field label="Suburb" className="col-span-3"><Input data-testid={`${prefix}suburb-input`} value={f[`${prefix}suburb`]} onChange={(e) => set(`${prefix}suburb`, e.target.value)} /></Field>
    <Field label="State" className="col-span-1"><StateSelect testId={`${prefix}state-select`} value={f[`${prefix}state`]} onChange={(v) => set(`${prefix}state`, v)} /></Field>
    <Field label="Postcode" className="col-span-2"><Input data-testid={`${prefix}postcode-input`} maxLength={4} value={f[`${prefix}postcode`]} onChange={(e) => set(`${prefix}postcode`, e.target.value.replace(/\D/g, ""))} /></Field>
  </div>
);

export const CustomerDialog = ({ open, onOpenChange, customer, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(customer ? { ...EMPTY, ...customer } : EMPTY); }, [open, customer]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const abnWarn = f.abn && !isValidABN(f.abn);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { id, created_at, ...body } = f;
      const r = customer?.id ? await api.put(`/customers/${customer.id}`, body) : await api.post("/customers", body);
      toast.success(customer?.id ? "Customer updated" : "Customer created");
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="customer-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{customer?.id ? "Edit customer" : "New customer"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="flex gap-2">
            {["business", "residential"].map((t) => (
              <button type="button" key={t} data-testid={`customer-type-${t}`} onClick={() => set("customer_type", t)}
                className={`rounded-md border px-4 py-1.5 text-sm font-medium capitalize transition-colors ${f.customer_type === t ? "border-red-600 bg-red-600 text-white" : "border-zinc-200 hover:border-zinc-400"}`}>{t}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {f.customer_type === "business" && <>
              <Field label="Business name"><Input data-testid="company-name-input" value={f.company_name} onChange={(e) => set("company_name", e.target.value)} /></Field>
              <Field label="ABN">
                <Input data-testid="abn-input" placeholder="XX XXX XXX XXX" value={f.abn} onChange={(e) => set("abn", e.target.value)} className="font-mono" />
                {abnWarn && <p data-testid="abn-warning" className="mt-1 text-xs text-orange-600">This ABN doesn't pass the checksum — please double check.</p>}
              </Field>
            </>}
            <Field label="Contact name *"><Input data-testid="contact-name-input" required value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} /></Field>
            <Field label="Email"><Input data-testid="email-input" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label="Phone"><Input data-testid="phone-input" placeholder="02 9xxx xxxx" value={f.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
            <Field label="Mobile"><Input data-testid="mobile-input" placeholder="04xx xxx xxx" value={f.mobile} onChange={(e) => set("mobile", e.target.value)} /></Field>
          </div>
          <div><div className="eyebrow mb-2">Billing address</div><AddressFields f={f} set={set} /></div>
          <div className="flex items-center gap-3">
            <Switch data-testid="site-same-switch" checked={f.site_same_as_billing} onCheckedChange={(v) => set("site_same_as_billing", v)} />
            <span className="text-sm">Site address same as billing</span>
          </div>
          {!f.site_same_as_billing && <div><div className="eyebrow mb-2">Site address</div><AddressFields f={f} set={set} prefix="site_" /></div>}
          <Field label="Notes"><Textarea data-testid="notes-input" rows={3} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="customer-save-button" disabled={saving} type="submit" className="bg-red-600 hover:bg-red-700">{saving ? "Saving…" : "Save customer"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
